import { PlayerNumber } from '~/modules/players/player-number';
import { ControlOption } from '~/routes/keyboard-help/controls';
import { DummyInputSource } from '~/routes/select-input/input-sources/dummy';
import { InputSourceList, InputSourceNames } from '~/routes/select-input/input-sources/interfaces';
import { RemoteMicrophoneInputSource } from '~/routes/select-input/input-sources/remote';
import { SlotState } from '~/routes/select-input/slot-state';

export interface InputOption {
  /** Opaque to everything but `decodeInputOption` - it is what crosses the wire to a phone. */
  value: string;
  label: string;
  source: InputSourceNames;
  deviceId: string;
  channel: number;
  kind: NonNullable<ControlOption['kind']>;
  /**
   * The slot already singing into this exact channel, when one is. A microphone is one voice: two
   * slots pointed at it score the same person twice, and neither the picker nor `changeInput` had
   * any notion of "taken" until this.
   */
  heldBy?: PlayerNumber;
}

/**
 * A unit separator rather than a printable one: a device id is whatever the browser hands back and a
 * phone id is whatever the relay assigns, so any character that could appear in a name would be
 * ambiguous on the way back.
 */
const SEPARATOR = '\u001f';

const encode = (source: InputSourceNames, deviceId: string, channel: number) =>
  [source, deviceId, channel].join(SEPARATOR);

/**
 * Every input a slot can be pointed at, one option per source *and* channel - a two-channel SingStar
 * device is two microphones, and picking "the device" would leave the second singer unreachable.
 *
 * `Dummy` is left out because it is the absence of an input, not one of them: it is what an empty
 * slot holds, and offering it as a choice is offering to empty the slot by picking a microphone.
 * Connected phones need no special case - `Remote Microphone`'s list *is* the connected phones, and
 * `useMicrophoneList` drops any source whose list is empty, so a phone joins and leaves the picker
 * on its own.
 */
export const inputOptions = (sources: Record<string, InputSourceList>, slots: SlotState[] = []): InputOption[] => {
  // Keyed by the same encoding the options carry, so the unit of "taken" is a CHANNEL rather than a
  // device: a two-channel SingStar is two microphones, and locking the device would make a duet on
  // one of them impossible.
  const holders = new Map<string, PlayerNumber>();
  for (const slot of slots) {
    const { selected } = slot;
    if (!selected?.deviceId || selected.source === DummyInputSource.inputName) continue;
    holders.set(encode(selected.source, selected.deviceId, selected.channel), slot.number);
  }

  return (Object.keys(sources) as InputSourceNames[])
    .filter((source) => source !== DummyInputSource.inputName)
    .flatMap((source) =>
      sources[source].list.map((input) => {
        const value = encode(source, input.deviceId, input.channel);

        return {
          value,
          // Already descriptive at source: "SingStar Wired - Blue", "MacBook Pro (ch 1)", and a phone
          // arrives as "📱Ana". Nothing to add here, and a source prefix would only repeat it.
          label: input.label,
          source,
          deviceId: input.deviceId,
          channel: input.channel,
          kind: inputKind(source, input.channels),
          heldBy: holders.get(value),
        };
      }),
    );
};

/**
 * The list as ONE slot sees it: an input another slot is singing into cannot be picked, and says
 * which slot has it - moving it means clearing that one, which is the only way to do it.
 *
 * `chosen` is what the slot is already on, and it is never locked. Two slots CAN legitimately share
 * one microphone - `addInput` puts them there on purpose when the room has nothing spare, since one
 * mic between two singers still scores them both - and only one of the pair can be recorded as the
 * holder. Without this the other would find its own input greyed out and attributed to its partner.
 *
 * The same list goes to the on-screen `Select` and into the mirrored descriptor, so the television
 * and the phone read identically rather than agreeing by coincidence.
 */
export const optionsForSlot = (options: InputOption[], slot: PlayerNumber, chosen = ''): ControlOption[] =>
  options.map(({ value, label, heldBy, kind, source, deviceId }) => ({
    value,
    label,
    kind,
    ...(source === RemoteMicrophoneInputSource.inputName && { micId: deviceId }),
    ...(heldBy !== undefined &&
      heldBy !== slot &&
      value !== chosen && { disabled: true, note: `Player ${heldBy + 1}` }),
  }));

// A two-channel device is a SingStar-style pair; a single channel is the machine's own microphone.
const inputKind = (source: InputSourceNames, channels: number): InputOption['kind'] =>
  source === RemoteMicrophoneInputSource.inputName ? 'phone' : channels > 1 ? 'mic' : 'computer';

/** Whether this slot may be pointed at `value` - the same rule `optionsForSlot` draws. */
export const canPickInput = (options: InputOption[], slot: PlayerNumber, value: string, chosen = '') => {
  const option = decodeInputOption(options, value);
  return option !== null && (option.heldBy === undefined || option.heldBy === slot || value === chosen);
};

/** The option a slot is currently on, or `''` when it is on something the list no longer offers. */
export const currentInputOption = (
  options: InputOption[],
  input: { source: InputSourceNames; deviceId?: string; channel: number } | null | undefined,
) => {
  if (!input?.deviceId) return '';

  const value = encode(input.source, input.deviceId, input.channel);

  return options.some((option) => option.value === value) ? value : '';
};

/** `null` for a value that is not one of `options` - a stale pick from a device that has gone. */
export const decodeInputOption = (options: InputOption[], value: string) =>
  options.find((option) => option.value === value) ?? null;
